import {
  AUDIT_ACTION_LABELS,
  AUDIT_HOT_DAYS,
  type AuditActionKey,
  type AuditEntryView,
  type AuditPage,
  describeAuditEntry,
  isAuditAction,
} from '@hoopoe/shared';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { ScrollText } from 'lucide-react';
import { type FormEvent, useEffect, useState } from 'react';
import { DataNumber, DataText } from '../components/data/data-text';
import { EmptyState } from '../components/guidance/empty-state';
import { ErrorState } from '../components/guidance/error-state';
import { FieldHelp } from '../components/guidance/field-help';
import { LearnMore } from '../components/guidance/learn-more';
import { useScreenTour } from '../components/guidance/tour';
import { PageHeader } from '../components/layout/app-shell';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { api } from '../lib/api';

// Admin → Audit log (task 27, PLAN §11): every view, edit, export, AI call and sign-in, newest first, filtered by
// person, kind of action, record and day. Filters live in the address, so a filtered view can be shared or linked
// (the profile's "Activity log" opens it for one entity).

export interface AuditSearch {
  user?: string;
  action?: AuditActionKey;
  target?: string;
  from?: string;
  to?: string;
}
const str = (v: unknown) => (typeof v === 'string' && v.trim() ? v.trim() : undefined);
const day = (v: unknown) => (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v) ? v : undefined);

export const Route = createFileRoute('/admin/audit')({
  validateSearch: (s: Record<string, unknown>): AuditSearch => ({
    user: str(s.user),
    action: isAuditAction(s.action) ? s.action : undefined,
    target: str(s.target),
    from: day(s.from),
    to: day(s.to),
  }),
  component: AuditPageScreen,
});

const TOUR = {
  id: 'admin-audit',
  steps: [
    {
      target: '[data-tour="audit-filters"]',
      title: 'Narrow it down',
      body: 'Filter by person, by kind of action, by record (paste an id, or a word like document) and by day. The address keeps the filters, so you can share the view.',
      learnMore: 'audit-log',
    },
    {
      target: '[data-tour="audit-results"]',
      title: 'Every entry',
      body: 'Newest first. Open an entry for its details: the address it came from, the browser, the request reference and what changed.',
    },
  ],
};

const when = (ms: number) =>
  new Date(ms).toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });

function AuditPageScreen() {
  useScreenTour(TOUR);
  const search = Route.useSearch();
  const navigate = useNavigate({ from: '/admin/audit' });
  const people = useQuery({
    queryKey: ['admin', 'audit', 'people'],
    queryFn: () => api<{ people: { id: string; email: string; name: string | null }[] }>('/admin/audit/people'),
  });
  const log = useInfiniteQuery({
    queryKey: ['admin', 'audit', search],
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) => {
      const qs = new URLSearchParams();
      for (const [k, v] of Object.entries(search)) if (v) qs.set(k, v);
      if (pageParam) qs.set('cursor', pageParam);
      return api<AuditPage>(`/admin/audit?${qs}`);
    },
    getNextPageParam: last => last.next,
  });
  const entries = log.data?.pages.flatMap(p => p.entries) ?? [];
  const oldestAt = log.data?.pages[0]?.oldestAt ?? null;
  const filtered = Object.values(search).some(Boolean);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Audit log"
        description="Who viewed, changed, exported or signed in, and when. Entries can’t be edited or deleted here."
      />
      <Filters search={search} people={people.data?.people ?? []} onChange={next => navigate({ search: next, replace: true })} />
      <section aria-label="Audit entries" data-tour="audit-results" className="space-y-3">
        {log.isError ? (
          <ErrorState error={log.error} title="The audit log couldn’t be loaded" onRetry={() => log.refetch()} learnMore="audit-log" />
        ) : log.isPending ? (
          <div className="space-y-2" aria-busy="true">
            <span className="sr-only">Loading…</span>
            {[0, 1, 2, 3, 4].map(i => (
              <div key={i} className="h-10 animate-pulse rounded-md bg-muted" />
            ))}
          </div>
        ) : entries.length === 0 ? (
          <EmptyState
            icon={ScrollText}
            title={filtered ? 'No entries match these filters' : 'Nothing recorded yet'}
            learnMore="audit-log"
            action={
              filtered ? (
                <Button variant="outline" size="sm" onClick={() => navigate({ search: {}, replace: true })}>
                  Clear filters
                </Button>
              ) : undefined
            }
          >
            {filtered
              ? `Try a wider date range or another person. Entries older than ${AUDIT_HOT_DAYS} days are in the monthly archive (Admin → Backups).`
              : 'Views, changes, exports and sign-ins appear here as people use Hoopoe.'}
          </EmptyState>
        ) : (
          <>
            <table className="w-full table-fixed border-collapse text-sm" aria-label="Audit entries">
              <thead className="text-start text-xs text-muted-foreground">
                <tr className="border-b">
                  <th scope="col" className="w-44 py-2 pe-3 text-start font-medium">
                    When
                  </th>
                  <th scope="col" className="w-52 py-2 pe-3 text-start font-medium">
                    Who
                  </th>
                  <th scope="col" className="py-2 pe-3 text-start font-medium">
                    What
                  </th>
                  <th scope="col" className="w-24 py-2 text-start font-medium">
                    <span className="sr-only">Details</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {entries.map(e => (
                  <EntryRow key={e.id} entry={e} />
                ))}
              </tbody>
            </table>
            <div className="flex flex-wrap items-center gap-3">
              {log.hasNextPage && (
                <Button variant="outline" size="sm" onClick={() => log.fetchNextPage()} disabled={log.isFetchingNextPage}>
                  {log.isFetchingNextPage ? 'Loading…' : 'Show older entries'}
                </Button>
              )}
              <p className="text-xs text-muted-foreground" role="status">
                Showing <DataNumber>{entries.length.toLocaleString()}</DataNumber> {entries.length === 1 ? 'entry' : 'entries'}
                {!log.hasNextPage && oldestAt && (
                  <>
                    . The log here starts <DataNumber>{when(oldestAt)}</DataNumber>; older months are in the archive (
                    <Link to="/admin/backups" className="underline">
                      Backups
                    </Link>
                    ).
                  </>
                )}
              </p>
            </div>
          </>
        )}
        <LearnMore slug="audit-log" />
      </section>
    </div>
  );
}

function Filters({
  search,
  people,
  onChange,
}: {
  search: AuditSearch;
  people: { id: string; email: string; name: string | null }[];
  onChange: (s: AuditSearch) => void;
}) {
  // The record box applies on submit (Enter or Apply); selects and dates apply at once.
  const [target, setTarget] = useState(search.target ?? '');
  useEffect(() => setTarget(search.target ?? ''), [search.target]);
  const set = (patch: Partial<AuditSearch>) => onChange({ ...search, ...patch });
  const submit = (ev: FormEvent) => {
    ev.preventDefault();
    set({ target: target.trim() || undefined });
  };
  const select = 'h-9 w-full rounded-md border border-input bg-surface px-2 text-sm';
  return (
    <form
      onSubmit={submit}
      aria-label="Filter the audit log"
      data-tour="audit-filters"
      className="grid gap-3 rounded-lg border bg-surface p-4 sm:grid-cols-2 lg:grid-cols-[1fr_1fr_1.4fr_auto_auto_auto]"
    >
      <label className="space-y-1 text-sm">
        <span className="font-medium">Who</span>
        <select aria-label="Who" className={select} value={search.user ?? ''} onChange={e => set({ user: e.target.value || undefined })}>
          <option value="">Everyone</option>
          {people.map(p => (
            <option key={p.id} value={p.id}>
              {p.name ? `${p.name} (${p.email})` : p.email}
            </option>
          ))}
        </select>
      </label>
      <label className="space-y-1 text-sm">
        <span className="font-medium">Action</span>
        <select
          aria-label="Action"
          className={select}
          value={search.action ?? ''}
          onChange={e => set({ action: isAuditAction(e.target.value) ? e.target.value : undefined })}
        >
          <option value="">All actions</option>
          {Object.entries(AUDIT_ACTION_LABELS).map(([k, label]) => (
            <option key={k} value={k}>
              {label}
            </option>
          ))}
        </select>
      </label>
      <div className="space-y-1 text-sm">
        <span className="flex items-center gap-1">
          <label htmlFor="audit-target" className="font-medium">
            Record
          </label>
          <FieldHelp
            label="Record"
            help="Part of what the entry is about: an entity or document id (from its address), or a kind such as document, entity, user or export. Press Enter to apply."
          />
        </span>
        <Input
          id="audit-target"
          type="search"
          value={target}
          onChange={e => setTarget(e.target.value)}
          placeholder="e.g. 01K6… or document"
        />
      </div>
      <div className="space-y-1 text-sm">
        <label htmlFor="audit-from" className="block font-medium">
          From
        </label>
        <Input
          id="audit-from"
          type="date"
          dir="ltr"
          value={search.from ?? ''}
          max={search.to}
          onChange={e => set({ from: e.target.value || undefined })}
        />
      </div>
      <div className="space-y-1 text-sm">
        <label htmlFor="audit-to" className="block font-medium">
          To
        </label>
        <Input
          id="audit-to"
          type="date"
          dir="ltr"
          value={search.to ?? ''}
          min={search.from}
          onChange={e => set({ to: e.target.value || undefined })}
        />
      </div>
      <div className="flex items-end gap-2">
        <Button type="submit" size="md">
          Apply
        </Button>
        <Button
          type="button"
          variant="ghost"
          onClick={() => {
            setTarget('');
            onChange({});
          }}
        >
          Clear
        </Button>
      </div>
    </form>
  );
}

function EntryRow({ entry: e }: { entry: AuditEntryView }) {
  const [open, setOpen] = useState(false);
  const detailsId = `audit-${e.id}`;
  return (
    <>
      <tr className="border-b align-top" data-testid="audit-entry">
        <td className="py-2 pe-3">
          <DataNumber className="text-xs">{when(e.at)}</DataNumber>
        </td>
        <td className="truncate py-2 pe-3">
          {e.userEmail ? (
            <span title={e.userEmail}>{e.userName ? <DataText>{e.userName}</DataText> : e.userEmail}</span>
          ) : (
            <span className="text-muted-foreground">System or signed out</span>
          )}
        </td>
        <td className="py-2 pe-3">
          <span className="me-2 rounded bg-muted px-1.5 py-0.5 text-xs font-medium">{AUDIT_ACTION_LABELS[e.action] ?? e.action}</span>
          <span className="break-all">{describeAuditEntry(e)}</span>
        </td>
        <td className="py-2">
          <Button variant="ghost" size="sm" aria-expanded={open} aria-controls={detailsId} onClick={() => setOpen(!open)}>
            {open ? 'Hide' : 'Details'}
          </Button>
        </td>
      </tr>
      {open && (
        <tr id={detailsId} className="border-b bg-muted/40">
          <td colSpan={4} className="p-3">
            <dl className="grid gap-x-4 gap-y-1 text-xs sm:grid-cols-[8rem_1fr]">
              <dt className="text-muted-foreground">Record</dt>
              <dd className="break-all font-mono">{e.target ?? '—'}</dd>
              <dt className="text-muted-foreground">Person</dt>
              <dd className="break-all">{e.userEmail ?? e.userId ?? '—'}</dd>
              <dt className="text-muted-foreground">IP address</dt>
              <dd className="font-mono">{e.ip ?? '—'}</dd>
              <dt className="text-muted-foreground">Browser</dt>
              <dd className="break-all">{e.ua ?? '—'}</dd>
              <dt className="text-muted-foreground">Reference</dt>
              <dd className="font-mono">{e.requestId ?? '—'}</dd>
              <dt className="text-muted-foreground">Details</dt>
              <dd>
                {e.meta ? (
                  <pre dir="auto" className="max-h-64 overflow-auto whitespace-pre-wrap break-all rounded bg-surface p-2 font-mono">
                    {JSON.stringify(e.meta, null, 2)}
                  </pre>
                ) : (
                  '—'
                )}
              </dd>
            </dl>
          </td>
        </tr>
      )}
    </>
  );
}
